import * as THREE from 'three';
import { CAMERA, COURT } from '../game/constants';
import { pseudoNoise } from '../utils/tweens';

export type CameraMode = 'broadcast' | 'celebrate';

export class CameraDirector {
  private readonly focus = new THREE.Vector3(0, 0, 0);
  private readonly desiredFocus = new THREE.Vector3(0, 0, 0);
  private readonly shakeOffset = new THREE.Vector3();
  private trauma = 0;
  private fovPunch = 0;
  private time = 0;
  private mode: CameraMode = 'broadcast';
  private modeTimer = 0;
  private reducedMotion = false;
  private readonly combatFocus = new THREE.Vector3();
  private combatTimer = 0;

  showImpact(position: THREE.Vector3): void {
    this.combatFocus.copy(position).setY(0);
    this.combatTimer = 0.75;
  }

  constructor(readonly camera: THREE.PerspectiveCamera) {
    this.camera.position.set(0, CAMERA.height, CAMERA.distance);
    this.camera.lookAt(0, 0.6, 0);
  }

  addTrauma(amount: number): void {
    this.trauma = Math.min(1, this.trauma + amount);
  }

  punchFov(amount: number): void {
    this.fovPunch = Math.min(1.4, this.fovPunch + amount);
  }

  setReducedMotion(enabled: boolean): void {
    this.reducedMotion = enabled;
  }

  snapTo(focus: THREE.Vector3, portrait: boolean): void {
    this.focus.copy(focus);
    this.desiredFocus.copy(focus);
    const height = portrait ? CAMERA.portraitHeight : CAMERA.height;
    const distance = portrait ? CAMERA.portraitDistance : CAMERA.distance;
    this.camera.position.set(focus.x, focus.y + height, focus.z + distance);
    this.camera.fov = portrait ? CAMERA.portraitFov : CAMERA.baseFov;
    this.camera.updateProjectionMatrix();
    this.camera.lookAt(focus.x, 0.6, focus.z);
  }

  setMode(mode: CameraMode): void {
    if (this.mode === mode) return;
    this.mode = mode;
    this.modeTimer = 0;
  }

  update(
    delta: number,
    ballPos: THREE.Vector3,
    ballVel: THREE.Vector3,
    hotPlayer: THREE.Vector3 | null,
    portrait: boolean,
    aspect: number,
  ): void {
    this.time += delta;
    this.modeTimer += delta;

    const height = portrait ? CAMERA.portraitHeight : CAMERA.height;
    const distance = portrait ? CAMERA.portraitDistance : CAMERA.distance;

    const lead = this.reducedMotion ? 0 : CAMERA.leadScale;
    this.desiredFocus.set(
      ballPos.x + ballVel.x * lead * 0.06,
      0,
      THREE.MathUtils.clamp(ballPos.z + ballVel.z * lead * 0.06, -COURT.halfWidth + 1.2, COURT.halfWidth - 1.2),
    );
    if (this.combatTimer > 0 && this.mode === 'broadcast') {
      this.combatTimer = Math.max(0, this.combatTimer - delta);
      this.desiredFocus.lerp(this.combatFocus, Math.min(0.7, this.combatTimer * 2));
    }
    if (hotPlayer && this.mode === 'celebrate') {
      this.desiredFocus.lerp(hotPlayer, 0.55);
    }
    this.desiredFocus.x = THREE.MathUtils.clamp(this.desiredFocus.x, -COURT.halfLength + 3.4, COURT.halfLength - 3.4);

    const lag = this.mode === 'celebrate' ? CAMERA.lag * 1.6 : CAMERA.lag;
    const k = this.reducedMotion ? 1 : 1 - Math.exp(-delta / Math.max(lag, 0.001));
    this.focus.lerp(this.desiredFocus, this.reducedMotion ? 1 : Math.min(1, k * 4.6));

    const celebrateDrop = this.mode === 'celebrate' ? 2.4 : 0;
    const combatCloseup = this.reducedMotion ? 0 : Math.min(1, this.combatTimer * 2);
    const desiredX = this.focus.x;
    const desiredY = height - celebrateDrop - combatCloseup * 1.1;
    const desiredZ = this.focus.z + distance - (this.mode === 'celebrate' ? 2.2 : 0) - combatCloseup * 2.2;

    const posK = this.reducedMotion ? 1 : 1 - Math.exp(-delta / Math.max(lag * 0.55, 0.001));
    this.camera.position.x += (desiredX - this.camera.position.x) * Math.min(1, posK * 5.4);
    this.camera.position.y += (desiredY - this.camera.position.y) * Math.min(1, posK * 5.4);
    this.camera.position.z += (desiredZ - this.camera.position.z) * Math.min(1, posK * 5.4);

    this.shakeOffset.set(0, 0, 0);
    if (!this.reducedMotion && this.trauma > 0.001) {
      const shake = this.trauma * this.trauma;
      const t = this.time * 26;
      this.shakeOffset.set(
        pseudoNoise(t, 1) * 0.52 * shake,
        pseudoNoise(t, 2) * 0.38 * shake,
        pseudoNoise(t, 3) * 0.3 * shake,
      );
      this.camera.position.add(this.shakeOffset);
      this.trauma = Math.max(0, this.trauma - delta * 2);
    } else {
      this.trauma = 0;
    }

    const lookY = 0.6 - (this.mode === 'celebrate' ? 0.3 : 0);
    this.camera.up.set(0, 1, 0);
    this.camera.lookAt(this.focus.x + this.shakeOffset.x * 0.5, lookY, this.focus.z + this.shakeOffset.z * 0.5);

    this.fovPunch = Math.max(0, this.fovPunch - delta * 1.9);
    const baseFov = this.mode === 'celebrate'
      ? (portrait ? CAMERA.portraitFov : CAMERA.baseFov) - 3
      : portrait ? CAMERA.portraitFov : CAMERA.baseFov;
    const wide = aspect < 1.15 ? 3 : 0;
    const targetFov = baseFov + wide - this.fovPunch * this.fovPunch * 7;
    this.camera.fov += (targetFov - this.camera.fov) * Math.min(1, delta * 6);
    this.camera.updateProjectionMatrix();
  }
}
